import { useEffect, useRef, useState } from 'react';

import type { FrameRect } from '../components/EventTriggerDialog/road/hooks/useSpriteAssets';
import { devError } from '../utils/log';

export interface SpriteSheetAsset {
  imageSrc: string;
  dataSrc: string;
  frameCount?: number;
  animationDuration?: number;
}

export interface SpriteSheetState {
  frames: FrameRect[];
  image: HTMLImageElement | null;
  ready: boolean;
  frameCount: number;
  animationDuration: number;
}

const EMPTY_STATE: SpriteSheetState = {
  frames: [],
  image: null,
  ready: false,
  frameCount: 0,
  animationDuration: 0,
};

/**
 * Preloads a sprite sheet (parses its frame data JSON and loads the image) and returns the state
 * the SpriteAnimation component expects. Pass `enabled: false` to skip/defer the preload.
 */
export function useSpriteSheet(asset: SpriteSheetAsset | null, enabled = true): SpriteSheetState {
  const [state, setState] = useState<SpriteSheetState>(EMPTY_STATE);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!enabled || !asset) return;

    cancelledRef.current = false;

    const preload = async () => {
      try {
        const { dataSrc, imageSrc } = asset;

        const data = JSON.parse(dataSrc) as {
          frames: Record<string, { frame: FrameRect }>;
          meta?: { size?: { w: number; h: number } };
        };
        const rawFrames = Object.keys(data.frames)
          .sort()
          .map((k) => data.frames[k].frame);

        if (cancelledRef.current) return;

        const image = await new Promise<HTMLImageElement>((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = imageSrc;
        });

        if (cancelledRef.current) return;

        // If the PNG was exported at a higher resolution than the JSON declares (e.g. @2x), scale
        // the source coordinates so drawImage reads from the correct pixels. w/h stay at their
        // logical (JSON) values so SpriteAnimation sizes the canvas correctly; srcW/srcH carry the
        // physical source dimensions for the drawImage source rectangle.
        const metaW = data.meta?.size?.w;
        const pixelScale = metaW && image.naturalWidth ? image.naturalWidth / metaW : 1;
        const frames =
          pixelScale === 1
            ? rawFrames
            : rawFrames.map((f) => ({
                x: Math.round(f.x * pixelScale),
                y: Math.round(f.y * pixelScale),
                w: f.w,
                h: f.h,
                srcW: Math.round(f.w * pixelScale),
                srcH: Math.round(f.h * pixelScale),
              }));

        setState({
          frames,
          image,
          ready: true,
          frameCount: asset.frameCount || frames.length,
          animationDuration: asset.animationDuration || 0,
        });
      } catch (e) {
        devError('useSpriteSheet: failed to preload sprite', e);
      }
    };

    void preload();

    return () => {
      cancelledRef.current = true;
    };
    // Re-runs when the asset reference changes — callers should pass a stable/module-level asset.
  }, [enabled, asset]);

  return state;
}
